import React from 'react';
import {
  Box,
  Button,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from '@mui/material';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import CustomTextInput from '../../../components/CustomTextInput';
import PhoneCountryCodeSelect from '../../../components/form/PhoneCountryCodeSelect';

const ApplicantDetailsValidation = Yup.object().shape({
  email: Yup.string()
    .required('Registered EmaraTax Email Address is required')
    .matches(/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/, 'Email must be a valid'),
  country_code: Yup.object()
    .shape({
      code: Yup.string(),
      label: Yup.string(),
    })
    .nullable(),
  mobile: Yup.string().nullable(),
  tin: Yup.string()
    .required('TIN (Tax Identifier Number) is required')
    .matches(/^1\d{9}$/, 'TIN to start with 1 and have 10 digits'),
  action: Yup.number().oneOf([1, 2, 3], 'Please select an action'),
});

export default function OnboardingApplicantDetailsForm({ onSubmit, isVerified = false, tinNumber = null, isLoading }) {
  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(ApplicantDetailsValidation),
    defaultValues: {
      email: '',
      country_code: null,
      mobile: '',
      tin: tinNumber ?? '',
      action: tinNumber ? 2 : 1,
    },
  });

  // Watch email and tin fields to control button state
  const email = useWatch({ control, name: 'email' });
  const tin = useWatch({ control, name: 'tin' });
  const action = useWatch({ control, name: 'action' });

  const handleFormSubmit = async (data) => {
    // Format phone number by combining country_code and mobile
    const phoneNumber =
      data.country_code?.code && data.mobile ? `${data.country_code.code}${data.mobile}` : data.mobile || null;

    // Call the API handler
    await onSubmit({
      tin: data.tin,
      email: data.email,
      mobile: phoneNumber,
      action: data.action,
    });
  };

  // Disable button when email or TIN is empty, or when already verified, or when loading
  const isButtonDisabled = !email || !tin || isLoading || isVerified;

  return (
    <Paper elevation={0} sx={{ p: 1 }}>
      <Typography variant="h6" mb={2}>
        Linking Details
        <Divider></Divider>
      </Typography>
      <Box component="form" onSubmit={handleSubmit(handleFormSubmit)}>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <CustomTextInput
              data-testid="applicant-email-input"
              name="email"
              control={control}
              errors={errors}
              label={'Registered EmaraTax E-mail Address'}
              helperText=""
              endAdornment={undefined}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <CustomTextInput
              data-testid="applicant-tin-input"
              name="tin"
              control={control}
              errors={errors}
              disabled={tinNumber}
              label={'TIN (Tax Identifier Number)'}
              helperText=""
              endAdornment={undefined}
              inputProps={{ maxLength: 10 }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 4 }}>
                <PhoneCountryCodeSelect
                  data-testid="applicant-country-code-select"
                  name="country_code"
                  control={control}
                  errors={errors}
                  label={'Country Code'}
                  readOnly={false}
                  onChange={() => {}}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 8 }}>
                <CustomTextInput
                  data-testid="applicant-mobile-number-input"
                  name="mobile"
                  control={control}
                  errors={errors}
                  label={'Registered Mobile Number'}
                  helperText=""
                  readOnly={false}
                  endAdornment={undefined}
                />
              </Grid>
            </Grid>
          </Grid>

          {tinNumber && (
            <Grid size={{ xs: 12, sm: 6 }}>
              <Stack direction="row" alignItems="center" spacing={2}>
                <FormLabel component="legend" sx={{ mb: 0, fontWeight: 600, mr: 0 }}>
                  Select the action:
                </FormLabel>
                <FormControl component="fieldset">
                  <Controller
                    name="action"
                    control={control}
                    render={({ field }) => (
                      <RadioGroup {...field} row>
                        <FormControlLabel value={2} control={<Radio />} label="Reverify" />
                        <FormControlLabel value={3} control={<Radio />} label="Delinking" />
                      </RadioGroup>
                    )}
                  />
                </FormControl>
              </Stack>
              {errors.action && (
                <Stack direction="row" justifyContent="center" alignItems="center" spacing={2}>
                  <Typography variant="caption" color="error">
                    {errors.action.message}
                  </Typography>
                </Stack>
              )}
            </Grid>
          )}
          <Grid size={{ xs: 12 }}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}>
              <Button
                data-testid="verify-applicant-details-button"
                type="submit"
                variant="contained"
                color="primary"
                disabled={isButtonDisabled}
              >
                {isLoading ? 'Verifying...' : 'Verify Applicant Details'}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Box>
    </Paper>
  );
}
